import { useRef } from "react";
import { Loc, t } from "@/locales";
import { useConfiguratorDomain } from "@/configurator/domain/hooks/useConfiguratorDomain";
import { useOfferDomain } from "@/configurator/domain/hooks/useOfferDomain";
import type { RendererKind } from "@/configurator/domain/models/types";
import { saveDataUrl } from "@/configurator/utils/download";
import type { Scene3D } from "@/configurator/utils/scene/Scene3D";
import { useARPresentationComponent } from "./useARPresentationComponent";
import type { ARPresentation } from "./useARPresentationComponent";

/**
 * jsPDF snapshots the canvas synchronously, so the screenshot is taken one
 * frame late on purpose: a texture or visibility change applied in the same
 * tick would otherwise be missing from the offer's product render.
 */
const SCREENSHOT_SETTLE_MS = 200;

const IMAGE_FILE_FALLBACK = "konfigurace";

export type ConfiguratorComponent = {
  title: string;
  renderer: RendererKind;
  hasChrome: boolean;
  fontFamily: string;
  activeModelPath: string | undefined;
  sceneRef: React.MutableRefObject<Scene3D | null>;
  ar: ARPresentation;
  onDownloadPDF: () => void;
  onDownloadModel: () => void;
  onDownloadImage: () => void;
};

/**
 * Wires the configurator shell: it owns the live scene handle (the only piece
 * of state on this screen) and turns the three toolbar actions into calls on
 * the domain layer.
 */
export function useConfiguratorComponent(
  rendererOverride?: RendererKind,
): ConfiguratorComponent {
  const configurator = useConfiguratorDomain();
  const offer = useOfferDomain();
  const sceneRef = useRef<Scene3D | null>(null);
  const ar = useARPresentationComponent(sceneRef);

  const onDownloadPDF = () => {
    void (async () => {
      await new Promise((resolve) => setTimeout(resolve, SCREENSHOT_SETTLE_MS));
      try {
        await offer.downloadOffer({
          pageUrl: window.location.href,
          screenshotDataUrl: sceneRef.current?.getScreenshot()?.dataUrl,
        });
      } catch (error) {
        console.error("[configurator] Could not build the offer PDF", error);
      }
    })();
  };

  const onDownloadModel = () => {
    void configurator.downloadModel().catch((error: unknown) => {
      console.error("[configurator] Could not download the model", error);
    });
  };

  const onDownloadImage = () => {
    const screenshot = sceneRef.current?.getScreenshot();
    if (!screenshot) {
      console.warn(
        "[configurator] Screenshot unavailable: the 3D scene is not rendering yet.",
      );
      return;
    }
    saveDataUrl(
      screenshot.dataUrl,
      `${configurator.productId ?? IMAGE_FILE_FALLBACK}.png`,
    );
  };

  return {
    title: configurator.title ?? t(Loc.configuratorTitleFallback),
    renderer: rendererOverride ?? configurator.renderer,
    hasChrome: configurator.hasChrome,
    fontFamily: configurator.fontFamily,
    activeModelPath: configurator.activeModel?.path,
    sceneRef,
    ar,
    onDownloadPDF,
    onDownloadModel,
    onDownloadImage,
  };
}
